import { createSlice } from "@reduxjs/toolkit";
import {
  createOrganization,
  getAllOrganizationalUsers,
  getOrganizations,
  loginAdminUser,
  searchOrganizationalUsers,
  searchOrganizations,
  updateAdminUserAccount,
  updateOrganization,
} from "../dispatcher/adminDispatcher";

interface adminAuthState {
  isAuthenticated: boolean;
  userId: string | null;
  email: string;
  authError: boolean;
  img: string | null;
  fullName: string;
  loading: boolean;
  loadingOrganizations: boolean;
  loadingOrganizationUsers: boolean;
  organizations: any;
  organizationUsers: any;
}

const initialState: adminAuthState = {
  email: "",
  loading: false,
  isAuthenticated: false,
  userId: null,
  authError: false,
  fullName: "",
  img: null,
  loadingOrganizations: false,
  loadingOrganizationUsers: false,
  organizations: [],
  organizationUsers: [],
};

// Create a slice for actions and reducers
export const adminAuthSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    adminAuthError: (state) => {
      sessionStorage.removeItem("accessAdminToken");
      sessionStorage.removeItem("refreshAdminToken");
      state.loading = false;
      state.authError = false;
      state.email = "";
      state.isAuthenticated = false;
      state.userId = null;
      state.fullName = "";
      state.img = null;
    },
    logout: (state) => {
      sessionStorage.removeItem("accessAdminToken");
      sessionStorage.removeItem("refreshAdminToken");
      state.loading = false;
      state.authError = false;
      state.email = "";
      state.isAuthenticated = false;
      state.userId = null;
      state.fullName = "";
      state.img = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAdminUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(loginAdminUser.fulfilled, (state, action) => {
        sessionStorage.setItem("accessAdminToken", action.payload.accessToken);
        sessionStorage.setItem(
          "refreshAdminToken",
          action.payload.refreshToken
        );
        state.isAuthenticated = true;
        state.loading = false;
        state.authError = false;
        state.img = action.payload.photo;
        state.email = action.payload.user.login;
        state.fullName = action.payload.user.fullName;
        state.userId = action.payload.user.id;
      })
      .addCase(loginAdminUser.rejected, (state) => {
        state.isAuthenticated = false;
        state.loading = false;
        state.img = null;
      })

      // Organization Dispatchers
      .addCase(getOrganizations.pending, (state) => {
        state.loadingOrganizations = true;
      })
      .addCase(getOrganizations.fulfilled, (state, action) => {
        state.loadingOrganizations = false;
        state.organizations = action.payload?.company;
      })
      .addCase(getOrganizations.rejected, (state) => {
        state.loadingOrganizations = false;
      })
      .addCase(createOrganization.pending, (state) => {
        state.loadingOrganizations = true;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.loadingOrganizations = false;
      })
      .addCase(createOrganization.rejected, (state) => {
        state.loadingOrganizations = false;
      })
      .addCase(updateOrganization.pending, (state) => {
        state.loadingOrganizations = true;
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.loadingOrganizations = false;
      })
      .addCase(updateOrganization.rejected, (state) => {
        state.loadingOrganizations = false;
      })
      .addCase(searchOrganizations.pending, (state) => {
        state.loadingOrganizations = false;
      })
      .addCase(searchOrganizations.fulfilled, (state, action) => {
        state.loadingOrganizations = false;
        state.organizations = action.payload?.company;
      })
      .addCase(searchOrganizations.rejected, (state) => {
        state.loadingOrganizations = false;
      })

      // Organization Users Dispatchers
      .addCase(getAllOrganizationalUsers.pending, (state) => {
        state.loadingOrganizationUsers = true;
      })
      .addCase(getAllOrganizationalUsers.fulfilled, (state, action) => {
        state.loadingOrganizationUsers = false;
        state.organizationUsers = action.payload.users;
      })
      .addCase(getAllOrganizationalUsers.rejected, (state) => {
        state.loadingOrganizationUsers = false;
      })
      .addCase(searchOrganizationalUsers.pending, (state) => {
        state.loadingOrganizationUsers = false;
      })
      .addCase(searchOrganizationalUsers.fulfilled, (state, action) => {
        state.loadingOrganizationUsers = false;
        state.organizationUsers = action.payload.users;
      })
      .addCase(searchOrganizationalUsers.rejected, (state) => {
        state.loadingOrganizationUsers = false;
      })

      .addCase(updateAdminUserAccount.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAdminUserAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.fullName = action.payload?.user?.fullName;
        state.img = action.payload?.user?.img;
      })
      .addCase(updateAdminUserAccount.rejected, (state) => {
        state.loading = false;
      });
  },
});

// Export actions that do not involve API calls
export const { adminAuthError, logout } = adminAuthSlice.actions;
// Export reducer
export default adminAuthSlice.reducer;
